import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit, signal, Signal, WritableSignal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { RequestService } from '../shared/services/request.service';
import { catchError, finalize, map, of, switchMap, tap } from 'rxjs';
import {
    IIroNamRequest,
    IroNamRequestStatus,
    EFileEntity,
    EMPTY_PATH_PREFIX,
    IUser,
    IS3File,
    ITeam
} from '@shs-digi-care-monorepo/interfaces';
import { select } from '@ngxs/store';
import {
    UrgentButtonComponent,
    RequestsStatusPillComponent,
    ActivityStreamComponent,
    UserState,
    ITableActivity,
    TeamState,
    SnackbarService,
    UserNamePipe,
    ReferralOverviewComponent,
    ReferralDetailDialogComponent,
    LoggingService
} from '@shs-digi-care-monorepo/frontend-lib';
import type {
    Referral,
    ReferralDetailDialogData,
    ReferralDetailDialogResult
} from '@shs-digi-care-monorepo/frontend-lib';
import { AttachmentService } from '../shared/services/attachment.service';
import { DealInformationDetail } from './components/forms/deal-information/deal-information.component';
import { OrderDetailsDetail } from './components/forms/order-details-detail/order-details-detail.component';
import { ContactInformationDetail } from './components/forms/contact-information-detail/contact-information-detail.component';
import { SubmissionInfoDetail } from './components/forms/submission-info/submission-info.component';
import { EmeaActivityStreamOperations } from './operations/activity-stream.operations';
import { CommentsComponent } from './components/comments/comments.component';
import { StatusStepperComponent } from './components/status-stepper/status-stepper.component';
import { ChangeStatusComponent } from './components/change-status/change-status.component';
import { AsyncPipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { Tabs } from './request-detail-tabs.enum';
import { ReferralService } from '../shared/services/referral.service';

@Component({
    selector: 'app-request-detail',
    templateUrl: './request-detail.component.html',
    imports: [
        TranslateModule,
        UrgentButtonComponent,
        DealInformationDetail,
        OrderDetailsDetail,
        ContactInformationDetail,
        SubmissionInfoDetail,
        RequestsStatusPillComponent,
        StatusStepperComponent,
        RouterModule,
        ActivityStreamComponent,
        CommentsComponent,
        ChangeStatusComponent,
        UserNamePipe,
        AsyncPipe,
        ReferralOverviewComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class RequestDetailComponent implements OnInit {
    private requestService = inject(RequestService);
    private referralService = inject(ReferralService);
    private attachmentService = inject(AttachmentService);
    private route = inject(ActivatedRoute);
    private translate = inject(TranslateService);
    private snackbar = inject(SnackbarService);
    private dialog = inject(MatDialog);
    private logger = inject(LoggingService);

    protected requestId: string | null = null;
    protected tabsEnum: typeof Tabs = Tabs;
    protected request: WritableSignal<IIroNamRequest | null> = signal(null);
    protected statusEnum: typeof IroNamRequestStatus = IroNamRequestStatus;
    protected me: Signal<IUser | null> = select(UserState.me);
    protected selectedTab: WritableSignal<Tabs> = signal(Tabs.REQUEST_INFORMATION);
    protected activityItems: WritableSignal<ITableActivity[]> = signal([]);
    protected teams: Signal<ITeam[]> = select(TeamState.emeaTeams);
    protected loading: WritableSignal<boolean> = signal(true);
    protected activitiesLoading: WritableSignal<boolean> = signal(true);
    protected transitioning: WritableSignal<boolean> = signal(false);
    protected attachments: WritableSignal<IS3File[]> = signal([]);
    protected referrals: WritableSignal<Referral[]> = signal([]);
    protected commentReloadTrigger: WritableSignal<number> = signal(0);
    protected isTaskOwner = select(UserState.isTaskOwner);

    public ngOnInit(): void {
        this.requestId = this.route.snapshot.paramMap.get('id');
        if (this.requestId) {
            this.initializeRequestData(this.requestId);
        }
    }

    private initializeRequestData(requestId: string): void {
        this.loadRequest(requestId);
        this.loadActivityStream(requestId);
        this.loadAttachments(requestId);
        this.loadReferrals(requestId);
    }

    protected setTab(tab: Tabs): void {
        this.selectedTab.set(tab);
    }

    protected onUrgentChange(isUrgent: boolean): void {
        if (!this.requestId) {
            this.snackbar.error('request.errors.error-no-request-id');
            return;
        }

        this.requestService
            .updateRequest(this.requestId, { isUrgent })
            .pipe(
                tap((updated) => this.request.set(updated)),
                catchError((error) => {
                    this.snackbar.error('request.form.error-updating');
                    throw error;
                })
            )
            .subscribe();
    }

    protected onTransitioning(isTransitioning: boolean): void {
        this.transitioning.set(isTransitioning);
        if (!isTransitioning && this.requestId) {
            this.loadRequest(this.requestId);
            this.loadActivityStream(this.requestId);
            this.loadReferrals(this.requestId);
            this.commentReloadTrigger.update((v) => v + 1);
        }
    }

    protected onDownloadAttachment(filePath: string): void {
        if (!this.requestId) return;
        this.attachmentService
            .downloadFile({ fileEntity: EFileEntity.Request, requestId: this.requestId, filePath })
            .pipe(
                catchError((_error) => {
                    this.snackbar.error('request.errors.error-downloading-attachment');
                    return [];
                })
            )
            .subscribe();
    }

    protected onDeleteAttachment(filePath: string): void {
        if (!this.requestId) return;
        this.attachmentService
            .deleteFile({ fileEntity: EFileEntity.Request, requestId: this.requestId, filePath })
            .pipe(
                tap(() => this.attachments.update((files) => files.filter((f) => f.Key !== filePath))),
                catchError((_error) => {
                    this.snackbar.error('request.errors.error-deleting-attachment');
                    return [];
                })
            )
            .subscribe();
    }

    private loadAttachments(requestId: string): void {
        this.attachmentService
            .listFiles({ fileEntity: EFileEntity.Request, requestId, prefix: EMPTY_PATH_PREFIX })
            .pipe(
                tap((files) => this.attachments.set(files)),
                catchError((_error) => {
                    this.snackbar.error('request.errors.error-loading-attachments');
                    return [];
                })
            )
            .subscribe();
    }

    private loadRequest(requestId: string): void {
        this.requestService
            .getRequestById(requestId)
            .pipe(
                tap((request) => {
                    this.request.set(request);
                }),
                catchError((error) => {
                    this.snackbar.error('request.errors.error-loading');
                    throw error;
                }),
                finalize(() => {
                    this.loading.set(false);
                })
            )
            .subscribe();
    }

    private loadActivityStream(requestId: string): void {
        this.requestService
            .getActivityStream(requestId, 1, 100)
            .pipe(
                map((response) => response.items),
                tap((activities) => {
                    this.activityItems.set(
                        EmeaActivityStreamOperations.mapActivities(activities, this.teams(), this.translate)
                    );
                }),
                catchError((error) => {
                    this.snackbar.error('request.errors.error-loading-activity-stream');
                    throw error;
                }),
                finalize(() => {
                    this.activitiesLoading.set(false);
                })
            )
            .subscribe();
    }

    private loadReferrals(requestId: string): void {
        this.referralService
            .getReferrals(requestId)
            .pipe(
                tap((referrals) => this.referrals.set(referrals)),
                catchError(() => {
                    this.referrals.set([]);
                    return of([]);
                })
            )
            .subscribe();
    }

    protected onReferralClicked(referral: Referral): void {
        const data: ReferralDetailDialogData = {
            referral,
            parentDisplayId: this.request()?.displayId ?? '',
            isTaskOwner: this.isTaskOwner(),
            supportsReferralReassignment: true
        };
        this.dialog
            .open<ReferralDetailDialogComponent, ReferralDetailDialogData, ReferralDetailDialogResult>(
                ReferralDetailDialogComponent,
                {
                    width: '600px',
                    height: '100vh',
                    position: { right: '0', top: '0' },
                    panelClass: 'sheet-panel-right',
                    hasBackdrop: true,
                    data
                }
            )
            .afterClosed()
            .pipe(
                switchMap((result) => {
                    if (!result || !this.requestId) {
                        return of(void 0);
                    }
                    if (result.action === 'closeReferral') {
                        this.logger.info('Closing referral', { requestId: this.requestId, referralId: referral.id });
                        return this.referralService.closeReferral(this.requestId, referral.id).pipe(
                            tap(() => this.loadReferrals(this.requestId!)),
                            catchError((error) => {
                                this.logger.error('Failed to close referral', { error: error?.message ?? error });
                                this.snackbar.error('request.errors.error-transitioning');
                                return of(void 0);
                            })
                        );
                    }
                    if (result.action === 'reassignReferral') {
                        this.logger.info('Reassigning referral', {
                            requestId: this.requestId,
                            referralId: referral.id
                        });
                        return this.referralService.reassignReferral(this.requestId, referral.id, result.payload).pipe(
                            tap(() => this.loadReferrals(this.requestId!)),
                            catchError((error) => {
                                this.logger.error('Failed to reassign referral', {
                                    error: error?.message ?? error
                                });
                                this.snackbar.error('request.errors.error-transitioning');
                                return of(void 0);
                            })
                        );
                    }
                    return of(void 0);
                })
            )
            .subscribe();
    }
}
